/*
  blogs:
  This function constructs an Alpine.js data object to manage the behavior and state of a blog pagination interface, integrating Swiper for dynamic navigation and handling asynchronous blog data fetching.

  - Parameters:
    - blogsPerPage: Specifies the number of blog articles to fetch per page.

  - Returns:
    - An object containing methods and properties to manage blog articles, pagination, and navigation.

  - Key Features:
    - initialSetup: Fetches initial blog categories and articles, setting up pagination controls based on the data retrieved.
    - buildSwiper: Initializes a Swiper instance for fluid navigation.
    - goNextPage and goPreviousPage: Functions to navigate forward or backward in the pagination.
    - ifActive: Checks if the current blog category matches the specified handle.
    - getDate: Formats a date string into a more readable format.

  The blogs object also tracks the loading state, the current set of articles, pagination details, and active blog category, allowing for a responsive and user-friendly blog browsing experience.
*/

const blogs = (blogsPerPage) => ({
  loading: true,
  blogCategory: [],
  articles: [],
  blogsPerPage,
  blogHandle: '',
  cursor: '',
  endCursor: '',
  startCursor: '',
  hasPreviousPage: true,
  hasNextPage: true,

  initialSetup: async function () {
    this.blogCategory = await fetchBlogsCategory(window.hidden_blogs);

    const { articles, endCursor, hasNextPage, hasPreviousPage, startCursor } = await fetchBlogs(
      this.blogsPerPage,
      this.blogHandle,
      this.cursor,
    );

    this.endCursor = endCursor;
    this.startCursor = startCursor;
    this.hasPreviousPage = hasPreviousPage;
    this.hasNextPage = hasNextPage;
    this.articles = articles;
  },

  buildSwiper() {
    this.swiperTabsInstance = new Swiper(this.$el.querySelector('.swiper[swiper-tab-bank]'), {
      slidesPerView: 'auto',
      freeMode: {
        momentum: false,
      },
    });
  },

  goNextPage() {
    const params =
      '?' +
      new URLSearchParams({
        page: this.page + 1,
        cursor: this.endCursor,
      }).toString();
    window.location.href = window.location.pathname + params;
  },

  goPreviousPage() {
    let params = '';
    if (this.page > 2)
      params =
        '?' +
        new URLSearchParams({
          page: this.page - 1,
          cursor: this.startCursor,
        }).toString();
    window.location.href = window.location.pathname + params;
  },

  ifActive(handle) {
    return handle === this.blogHandle;
  },

  getDate(dateString) {
    const date = new Date(dateString);

    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.toLocaleString('default', { year: 'numeric' });
    const day = date.toLocaleString('default', { day: 'numeric' });

    return `${month} ${day}, ${year}`;
  },

  init: async function () {
    this.blogHandle = window.location.pathname.split('/')[2];

    this.page = new URLSearchParams(window.location.search).get('page')
      ? Number(new URLSearchParams(window.location.search).get('page'))
      : 1;

    this.cursor = new URLSearchParams(window.location.search).get('cursor') ?? '';

    await this.initialSetup();
    this.buildSwiper();
    this.loading = false;
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('blogs', blogs);
});
